import { useMemo } from "react";
import { useAppSelector } from "../redux/hooks";
import {
  adminRoutes,
  expeditionRoutes,
  fleetRoutes,
  reportsRoutes,
  transportRoutes,
} from "./routesConstants";

import userRoles from "../constants/userRoles";
import type { SvgIconType } from "../helpers/SvgIcon";

export interface AppMenuRoute {
  id: string;
  title: string;
  path: string;
  icon: SvgIconType;
  activePaths?: string[];
  subtitle?: string;
  value?: string | number;
  isIncreasing?: boolean;
}

export interface DashboardCategories {
  color: string;
  title: string;
  icon: SvgIconType;
  subtitle?: string;
  subtitleValue?: string;
  categories: AppMenuRoute[];
  navigationLinks?: {
    link: string;
    label: string;
  }[];
}

function useAppMenuRoutes() {
  const userRoutesAccess = useAppSelector(
    (state) => state.user.userRoutesAccess,
  );

  const filterAccessibleRoutes = (routes: AppMenuRoute[]) =>
    routes.filter((route) => userRoutesAccess?.includes(route.id));

  const expeditionMenuRoutes = filterAccessibleRoutes([
    {
      id: userRoles.AWB_HISTORY,
      title: "Expeditii active",
      icon: "NAV_ACTIVE_SHIPMENTS",
      path: `/${expeditionRoutes.activeExpeditions}`,
    },
    {
      id: userRoles.SCHEDULE_WORKING_POINTS,
      title: "Program locatii de livrare",
      icon: "NAV_DELIVERY_SCHEDULE",
      path: `/${expeditionRoutes.workingPoints}`,
    },
    {
      id: userRoles.ROUTES_ORGANIZATION,
      title: "Organizare rute",
      icon: "NAV_ROUTES",
      activePaths: [`/${expeditionRoutes.routesOrganizationInternode}`],
      path: `/${expeditionRoutes.routesOrganizationLocal}`,
    },
    {
      id: userRoles.NODES,
      title: "Organizare noduri",
      icon: "NAV_NODES",
      path: `/${expeditionRoutes.nodes}`,
    },
    {
      id: userRoles.CLIENTS,
      title: "Clienti",
      icon: "NAV_CLIENTS",
      path: `/${expeditionRoutes.clients}`,
    },
    {
      id: userRoles.CREATE_BIN,
      title: "Formeaza BIN",
      icon: "NAV_CREATE_BIN",
      path: `/${expeditionRoutes.createBin}`,
    },
    {
      id: userRoles.VIEW_CARGO_VOLUME,
      title: "Vizualizare volum marfa",
      icon: "NAV_CARGO_OVERVIEW",
      path: `/${expeditionRoutes.viewCargoVolume}`,
    },
    {
      id: userRoles.BINS_VOLUME,
      title: "Volume marfa",
      icon: "NAV_CARGO_VOLUMES",
      path: `/${expeditionRoutes.binsVolume}`,
    },
    {
      id: userRoles.OVERSIZED_PACKAGES,
      title: "Colete agabaritice",
      icon: "NAV_OVERSIZED_PACKAGES",
      path: `/${expeditionRoutes.oversizedPackages}`,
    },
  ]);

  const transportMenuRoutes = filterAccessibleRoutes([
    {
      id: userRoles.ASSIGN_ROUTE,
      title: "Alocare ruta",
      icon: "NAV_ASSIGN_ROUTE",
      path: `/${transportRoutes.assign}`,
    },
    {
      id: userRoles.ACTIVE_TRANSPORTS,
      title: "Transporturi active",
      icon: "NAV_ACTIVE_TRANSPORTS",
      path: `/${transportRoutes.activeTransports}`,
    },
    {
      id: userRoles.ACTIVE_TRANSPORTS,
      title: "Istoric transporturi",
      icon: "NAV_TRANSPORT_HISTORY",
      path: `/${transportRoutes.transportHistory}`,
    },
  ]);

  const reportsMenuRoutes = filterAccessibleRoutes([
    {
      id: userRoles.SEARCH_INVOICES_AWB,
      title: "Cauta expeditii dupa facturi",
      icon: "NAV_INVOICE_SEARCH",
      path: `/${reportsRoutes.searchInvoicesAwb}`,
    },
    {
      id: userRoles.CARGO_NODE,
      title: "Colete in nod",
      icon: "NAV_NODE_CARGO",
      path: `/${reportsRoutes.cargoNode}`,
    },
    {
      id: userRoles.CARGO_CAR,
      title: "Colete in masina",
      icon: "NAV_VEHICLE_CARGO",
      path: `/${reportsRoutes.cargoCar}`,
    },
    {
      id: userRoles.VEHICLE_TEMPERATURE_CHART,
      title: "Diagrama temperatura autovehicul",
      icon: "NAV_VEHICLE_TEMPERATURE",
      path: `/${reportsRoutes.vehicleTemperatureChart}`,
    },
    {
      id: userRoles.NODE_TEMPERATURE_CHART,
      title: "Diagrama temperatura noduri",
      icon: "NAV_NODE_TEMPERATURE",
      path: `/${reportsRoutes.nodeTemperatureChart}`,
    },
    {
      id: userRoles.UNDELIVERED_PACKAGES_CENTRALIZER,
      title: "Centralizator colete nelivrate",
      icon: "NAV_UNDELIVERED_REPORT",
      path: `/${reportsRoutes.undeliveredPackagesCentralizer}`,
    },
    {
      id: userRoles.PACKAGE_HISTORY,
      title: "Istoric colet",
      icon: "NAV_PACKAGE_HISTORY",
      path: `/${reportsRoutes.packageHistory}`,
    },
    {
      id: userRoles.TEMPERATURE_ALERTS,
      title: "Alerte temperatura",
      icon: "NAV_TEMPERATURE_ALERTS",
      path: `/${reportsRoutes.temperatureAlerts}`,
    },
    {
      id: userRoles.PICKUP_CARGO_INTERNODE_ROUTE,
      title: "Marfa de ridicat pe ruta internod",
      icon: "NAV_PICKUP_CARGO",
      path: `/${reportsRoutes.pickupCargoInternodeRoute}`,
    },
  ]);

  const fleepMenuRoutes = filterAccessibleRoutes([
    {
      id: userRoles.PREVIEW_FLEET,
      title: "Flota",
      icon: "NAV_FLEET",
      path: `/${fleetRoutes.fleet}`,
    },
    {
      id: userRoles.CAR_DOCUMENTS_ALL_DISPLAY,
      title: "Documente masini",
      icon: "NAV_VEHICLE_DOCUMENTS",
      path: `/${fleetRoutes.carsDocuments}`,
    },
    {
      id: userRoles.ASSIGNED_CAR_NODE_DISPLAY,
      title: "Asignare masina la nod",
      icon: "NAV_CAR_NODE_ASSIGNMENT",
      path: `/${fleetRoutes.assignedCarNode}`,
    },
  ]);

  const adminMenuRoutes = filterAccessibleRoutes([
    {
      id: userRoles.PREVIEW_USERS,
      title: "Utilizatori definiti in sistem",
      icon: "NAV_USERS",
      path: `/${adminRoutes.users}`,
    },
    {
      id: userRoles.ADMINISTRATE_REASON,
      title: "Nomenclator motive",
      icon: "NAV_REASONS",
      path: `/${adminRoutes.administrateReason}`,
    },
    {
      id: userRoles.SERVICES,
      title: "Administrare servicii prestate",
      icon: "NAV_SERVICES",
      path: `/${adminRoutes.services}`,
    },
    {
      id: userRoles.GENERATE_BARCODE,
      title: "Genereaza coduri de bare",
      icon: "BARCODE",
      path: `/${adminRoutes.generateBarcode}`,
    },
    {
      id: userRoles.CUSTOMER_NOTIFICATIONS,
      title: "Notificari clienti",
      icon: "NAV_CUSTOMER_NOTIFICATIONS",
      path: `/${adminRoutes.customer_notifications}`,
    },
  ]);

  const categories: DashboardCategories[] = useMemo(
    () =>
      (
        [
          {
            color: "primary",
            title: "Flota",
            icon: "NAV_FLEET",
            categories: fleepMenuRoutes,
          },
          {
            color: "primary",
            title: "Expeditie",
            icon: "NAV_EXPEDITION",
            categories: expeditionMenuRoutes,
          },
          {
            color: "primary",
            title: "Transport",
            icon: "NAV_TRANSPORT",
            categories: transportMenuRoutes,
          },
          {
            color: "primary",
            title: "Rapoarte",
            icon: "NAV_REPORTS",
            categories: reportsMenuRoutes,
          },
          {
            color: "primary",
            title: "Administrare",
            icon: "NAV_ADMINISTRATION",
            categories: adminMenuRoutes,
          },
        ] satisfies DashboardCategories[]
      ).filter((category) => category.categories.length > 0),
    [
      adminMenuRoutes,
      fleepMenuRoutes,
      expeditionMenuRoutes,
      transportMenuRoutes,
      reportsMenuRoutes,
    ],
  );

  return categories;
}

export default useAppMenuRoutes;
